import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FlatList, Pressable, Text, View, RefreshControl } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useFocusEffect } from '@react-navigation/core';
import { useColorScheme } from 'nativewind';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { PostFilter, EventCard, Layout, Loader, Pagination } from '../../components';
import {
  adaptiveContainerPaddingTop,
  isMobile,
  isMobileWeb,
  isNative,
  isTablet,
  isWeb,
  navHeight,
} from '../../utils/responsive';
import { Article, PaginatedResponse } from '../../client/interfaces';
import client from '../../client/client';
import { useMessage } from '../../contexts/Messages/MessageContext';
import {
  NavigationParamList,
  ScreenName,
  StackName,
  StackNavigationScreenProps,
} from '../../navigation/types';
import { distributeElements } from '../../utils';
import { cln } from '../../utils/classnames';
import { useResize } from '../../utils/resize';
import { ArticlesParamsContext } from '../../contexts/ArticlesParams/ArticlesParamsContext';
import { NavigationContextStack } from '../../contexts/NavigationContext/NavigationContextStack';
import useDebounce from '../../hooks/useDebounce';
import analytics from '../../utils/analytics';

const ArticlesScreen: React.FC<
  StackNavigationScreenProps<NavigationParamList, ScreenName.ArticlesScreen>
> = ({ route, navigation }) => {
  const { t } = useTranslation();
  const { setMessage } = useMessage();
  const { isWidthBelowSmall, isWidthBelowLarge } = useResize();

  const insets = useSafeAreaInsets();

  const { stackRoute, setStackNavigation, setStackRoute, changedStack, setChangedStack } =
    useContext(NavigationContextStack);

  const [firstPageLoaded, setFirstPageLoaded] = useState<boolean>(false);
  const [needsRefresh, setNeedsRefresh] = useState(true);
  const initialMountRef = useRef(true);

  useEffect(() => {
    //We only clear the articles state when the changed stack is the one we're on
    //and if we have come from articlesScreen screen (not the event screen).
    if (stackRoute && changedStack === StackName.ArticlesStack) {
      if (!isMobile || (isMobile && stackRoute?.name === 'articles')) {
        emptyArticlesState();
        setFirstPageLoaded(false);
        isMobile && fetchArticles(afterMobileFetch);
      }
      setChangedStack(null);
      setNeedsRefresh(false);
    }
  }, [changedStack, stackRoute]);

  useFocusEffect(
    useCallback(() => {
      setStackRoute(route);
      setStackNavigation(navigation);
    }, [changedStack]),
  );

  // Reset the filter when leaving to another stack not when returning
  useEffect(() => {
    const unsubscribe = navigation.getParent()?.addListener('blur', () => {
      emptyArticlesFilter();
      if (isMobile) setNeedsRefresh(true);
    });
    return unsubscribe;
  }, [navigation]);

  const {
    getArticlesParams,
    setGetArticlesParams,
    nextPageIdentifier,
    resetInfiniteScroll,
    setNextPageIdentifier,
    allArticles,
    setAllArticles,
    emptyGetArticlesParams,
    emptyArticlesFilter,
    articlePagination,
    setArticlePagination,
    resetArticlePagination,
  } = useContext(ArticlesParamsContext);

  const [refreshing, setRefreshing] = useState(false);
  const { colorScheme } = useColorScheme();

  const [isLoading, setIsLoading] = useState(true);
  const [distributedArticles, setDistributedArticles] = useState<Article[][]>([]);
  const [paginatedArticles, setPaginatedArticles] = useState<Article[]>([]);

  const isFilterActive =
    getArticlesParams?.tags?.length >= 1 ||
    getArticlesParams?.isBookmarked ||
    getArticlesParams?.search;

  const isResetPending = isMobile && needsRefresh;

  const flatListRef = useRef<FlatList>(null);
  const debouncedSearch = useDebounce(getArticlesParams.search);
  const lastLoggedSearch = useRef<string | null>(null);

  const fetchArticles = async (
    afterFetchCallback: (paginatedArticles: PaginatedResponse<Article>) => void,
  ) => {
    try {
      setIsLoading(true);
      const paginatedArticlesResponse = await client.getArticles(getArticlesParams);
      afterFetchCallback(paginatedArticlesResponse);
      setArticlePagination(paginatedArticlesResponse.pagination);
      setDistributedArticles(
        distributeElements(
          paginatedArticlesResponse.items,
          isWidthBelowSmall ? 1 : isWidthBelowLarge ? 2 : 3,
        ),
      );

      if (getArticlesParams.search) {
        if (lastLoggedSearch.current !== getArticlesParams.search) {
          lastLoggedSearch.current = getArticlesParams.search;
          await analytics().logEvent('search', {
            search_term: getArticlesParams.search,
            content_type: 'article',
            results_count: paginatedArticlesResponse.pagination.total,
            has_results: paginatedArticlesResponse.pagination.total > 0 ? 1 : 0,
          });
        }
      } else {
        lastLoggedSearch.current = null;
      }
    } catch (error) {
      setMessage({ message: error.message, type: 'error' });
    }
    setTimeout(() => {
      setIsLoading(false);
      !firstPageLoaded && setFirstPageLoaded(true);
    }, 200);
  };

  useEffect(() => {
    setDistributedArticles(
      distributeElements(paginatedArticles, isWidthBelowSmall ? 1 : isWidthBelowLarge ? 2 : 3),
    );
  }, [isWidthBelowLarge, isWidthBelowSmall]);

  const afterMobileFetch = async (paginatedArticles: PaginatedResponse<Article>) => {
    const shouldAppend = getArticlesParams.page === nextPageIdentifier;

    setAllArticles((prev) => {
      const existingIds = new Set(prev.map((a) => a.id));
      const incomingIds = new Set(paginatedArticles.items.map((a) => a.id));

      const newItems = paginatedArticles.items.filter((a) => !existingIds.has(a.id));

      // Ha append (scroll), csak fűzzük az újakat
      if (shouldAppend) {
        return [...prev, ...newItems];
      }

      // Ha page === 1 és nem appendelünk, akkor új keresés történt
      // Megtartjuk azokat a régi elemeket, amik benne vannak az új találatokban
      const preservedItems = prev.filter((a) => incomingIds.has(a.id));

      // Új lista: megtartott + újonnan érkezett, de nem duplikált
      return [...preservedItems, ...newItems];
    });
  };

  const afterDesktopFetch = async (paginatedArticles: PaginatedResponse<Article>) => {
    setPaginatedArticles(paginatedArticles.items);
  };

  const toggleBookmark = (article: Article) => {
    const setter = isMobile ? setAllArticles : setPaginatedArticles;

    setter((prev) =>
      prev.map((a) => (a.id === article.id ? { ...a, isBookmarked: !a?.isBookmarked } : a)),
    );

    if (!isMobile) {
      setDistributedArticles((prev) =>
        prev.map((row) =>
          row.map((a) => (a.id === article.id ? { ...a, isBookmarked: !a?.isBookmarked } : a)),
        ),
      );
    }
  };

  const onScrollEndReached = () => {
    const nextPage = articlePagination.currentPage + 1;

    if (nextPage <= articlePagination.pageCount) {
      setNextPageIdentifier(nextPage);
    }
  };

  useEffect(() => {
    if (isMobile && initialMountRef.current) {
      initialMountRef.current = false;
      return;
    }
    isMobile ? fetchArticles(afterMobileFetch) : fetchArticles(afterDesktopFetch);
  }, [JSON.stringify(getArticlesParams)]);

  // Keep a live ref to fetchArticles so the focus-effect refetch below uses the
  // current params instead of the empty ones captured on first render (which
  // caused an unfiltered list after returning from an article on web).
  const fetchArticlesRef = useRef(fetchArticles);
  fetchArticlesRef.current = fetchArticles;

  useFocusEffect(
    useCallback(() => {
      if (!isMobile) {
        setPaginatedArticles([]);
        fetchArticlesRef.current(afterDesktopFetch);
      }
    }, []),
  );

  useEffect(() => {
    if (isMobile) {
      flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
    }
  }, [getArticlesParams.search, getArticlesParams.tags, getArticlesParams.isBookmarked]);

  useEffect(() => {
    if (nextPageIdentifier > articlePagination.currentPage) {
      setGetArticlesParams((prev) => ({ ...prev, page: nextPageIdentifier }));
    }
  }, [nextPageIdentifier]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchArticles(afterMobileFetch);
    setTimeout(() => {
      setRefreshing(false);
    }, 800);
  }, [debouncedSearch]);
  // DebounceSearch azért kellett mert az előző állapotot nézte amit már egyszer letett a paraméterekbe, megvárjuk a delayt

  const emptyArticlesState = (hard = true) => {
    setIsLoading(true);
    hard && setGetArticlesParams({ ...emptyGetArticlesParams });
    setAllArticles([]);
    setPaginatedArticles([]);
    resetInfiniteScroll();
    resetArticlePagination();
  };

  const renderArticleItem = useCallback(
    ({ item }) => (
      <EventCard
        item={item}
        toggleCardMark={toggleBookmark}
        styleProp={{
          flex: isTablet ? 0.5 : 1,
          marginBottom: 40,
        }}
      />
    ),
    [toggleBookmark, isTablet],
  );

  const mobileFeed =
    (isLoading && !firstPageLoaded) || isResetPending ? (
      <View className={cln('w-full h-full items-center justify-center', isTablet && 'pb-[320px]')}>
        <Loader />
      </View>
    ) : (
      <View
        style={{
          flex: 1,
          overflow: 'hidden',
        }}
        testID="articles-screen-mobile"
      >
        <FlatList
          data={allArticles}
          keyExtractor={(item, index) => `${item.id}-${index}`}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              progressViewOffset={-10}
              tintColor={colorScheme === 'dark' ? '#b0b0b0' : '#d1d1d1'}
              colors={colorScheme === 'dark' ? ['#b0b0b0'] : ['#f6f6f6']}
              progressBackgroundColor={
                colorScheme === 'dark' ? 'rgba(79,79,79,0.9)' : 'rgba(136, 136, 136,0.9)'
              }
            />
          }
          renderItem={renderArticleItem}
          onEndReached={onScrollEndReached}
          onEndReachedThreshold={0.8}
          initialNumToRender={emptyGetArticlesParams.perPage}
          ListFooterComponent={isLoading ? Loader : null}
          ref={flatListRef}
          numColumns={isTablet ? 2 : 1}
          columnWrapperStyle={
            isTablet ? { flex: 1, justifyContent: 'space-between', gap: 24 } : null
          }
          contentContainerStyle={{
            paddingHorizontal: 30,
            // Ez azert kell hogy betudjunk scrollozni a header moge
            paddingTop:
              !isTablet && !isFilterActive
                ? navHeight + insets.top + adaptiveContainerPaddingTop
                : 8,
          }}
        />
      </View>
    );

  const desktopFeed = isLoading ? (
    <Loader />
  ) : (
    <View testID="articles-screen-web-desktop">
      <View
        className="flex flex-row flex-wrap md:mx-[24px]"
        style={{
          rowGap: 64,
          columnGap: 24,
          justifyContent: 'flex-start',
          marginTop: isMobileWeb ? 24 : 0,
        }}
      >
        {distributedArticles.map((column, index) => (
          <View className={cln('flex-1')} key={index}>
            {column.map((article) => (
              <EventCard
                key={article.id}
                item={article}
                toggleCardMark={toggleBookmark}
                styleProp={{ marginBottom: isMobile ? 40 : 64 }}
              />
            ))}
          </View>
        ))}
      </View>
      <Pagination
        data={articlePagination}
        paramSetter={setGetArticlesParams}
        params={getArticlesParams}
      />
    </View>
  );

  const feed = isMobile ? mobileFeed : desktopFeed;

  const emptySearch = (
    <>
      {!isLoading && (
        <View
          className={cln('flex-column px-[32px]', isMobile && !isFilterActive && 'pt-[120px]')}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          style={{ display: isWeb ? 'inline' : 'flex' }}
        >
          <Text className="text-h3 md:text-h2 text-primaryLight-500 dark:text-primary-500 font-[sans-600]">
            {t('articles:no_results')}{' '}
          </Text>
          <Text className="text-h3 md:text-h2 text-neutral-950 dark:text-neutral-50 font-[sans-600]">
            {t('articles:based_on_your_search')}.
          </Text>
        </View>
      )}
    </>
  );
  return (
    <Layout
      title={!isMobile && t('articles:title')}
      isScrollView={!isMobile}
      onPressSearch={() =>
        navigation.navigate(ScreenName.PostFilterScreen, {
          fromScreen: ScreenName.ArticlesScreen,
        })
      }
      isListScreen={true}
    >
      {(getArticlesParams?.tags?.length >= 1 ||
        getArticlesParams.isBookmarked ||
        getArticlesParams.search) &&
      !isTablet &&
      isMobile ? (
        <View
          className="flex-row self-end px-[30px]"
          style={{
            paddingTop: isNative ? navHeight + insets.top : isMobile ? 56 : 0,
          }}
        >
          <Text className="text-neutral-950 dark:text-neutral-50 text-body font-[sans-700] mr-1.5">
            {articlePagination.total}
          </Text>
          <Text className="text-neutral-950 dark:text-neutral-50 text-body font-[sans-400]">
            {t('articles:results')}
          </Text>
        </View>
      ) : (
        <></>
      )}
      {/* REMOVE ALL BUTTON */}
      {(getArticlesParams?.tags?.length >= 1 ||
        getArticlesParams.isBookmarked ||
        getArticlesParams.search) &&
      !isTablet &&
      isMobile ? (
        <View className="px-[30px]">
          <Pressable
            className="justify-center items-center"
            onPress={emptyArticlesFilter}
            style={{
              alignSelf: 'flex-end',
            }}
          >
            <Text className="text-primary-600 dark:text-primary-500 text-body font-[sans-400] py-2">
              {t('articles:remove_search')}
            </Text>
          </Pressable>
        </View>
      ) : (
        <></>
      )}

      {!isMobile || isTablet ? (
        <PostFilter
          getPostsParams={getArticlesParams}
          setGetPostsParams={setGetArticlesParams}
          resetInfiniteScroll={resetInfiniteScroll}
          pagination={articlePagination}
          screen={ScreenName.ArticlesScreen}
        />
      ) : (
        <></>
      )}
      {firstPageLoaded && articlePagination.total === 0 ? emptySearch : feed}
    </Layout>
  );
};

export default ArticlesScreen;
